import { /*Link,*/ useNavigate } from "react-router-dom";
import { useRef, FormEvent, useState } from "react";
import { useUser } from "../Context/UserProvider";
import { useLoading } from "../Context/LoadingProvider";
import { LaravelValidationError } from "../Api/request";
import Logo from "../Assets/gfx/boolanga_bi_2024.webp";
import { useTranslation } from "react-i18next";
import LanguageSelector from "./LanguageSelector";

interface LoginProps {}

const Login: React.FunctionComponent<LoginProps> = () => {
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  const [validationErrors, setValidationErrors] = useState<Record<
    string,
    string[]
  > | null>(null);
  const navigate = useNavigate();
  const { login } = useUser();

  const { startLoading, finnishLoading } = useLoading();

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();

    if (emailRef.current && passwordRef.current) {
      startLoading("login-submit");
      login(emailRef.current.value, passwordRef.current.value)
        .then(() => {
          let isFirstLogin = localStorage.getItem("isFirstLogin");

          if(isFirstLogin === '1'){
              localStorage.removeItem("isFirstLogin");
              navigate("/reset-password");
              return;
          }

          navigate("/menu");
        })
        .catch((error: LaravelValidationError) => {
          setValidationErrors(error.errors);
        })
        .finally(() => {
          finnishLoading("login-submit");
        });
    }
  };

  return (
    <div className="view-container view-container--stretch background-login">

      <div className="view-content flex h-full flex-col justify-between">
        <div className="flex items-center justify-center" style={{height: '40dvh'}}>
          <div className="brand-logo-container">
            <img className="brand-logo" src={Logo} alt="Wear your brand logo" />
          </div>
        </div>
        <div className="">
          <div className="flex justify-between mb-4">
            <div>
              <div className="heading--medium">
                {t("Sign In")}
              </div>
              <div className="text-secondary">
                {t("Please login to your account")}
              </div>
            </div>
            <div>
              <LanguageSelector />
            </div>

          </div>
          
          <form onSubmit={handleLogin} className="w-full">
            <div className="mb-4">
              
              <div className="relative">

                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="input-icon">
                <path d="M0.979106 13.5175C1.05951 14.1134 1.34888 14.6662 1.8005 15.0867C2.25212 15.5071 2.83963 15.7706 3.46819 15.8347C5.25437 16.0135 7.10371 16.2 9.0001 16.2C10.8951 16.2 12.7458 16.0122 14.532 15.8347C15.1607 15.771 15.7485 15.5077 16.2002 15.0872C16.6519 14.6667 16.9411 14.1136 17.0211 13.5175C17.2119 12.0561 17.4001 10.5463 17.4001 8.99935C17.4001 7.45371 17.2119 5.94256 17.0211 4.48123C16.941 3.88504 16.6517 3.33193 16.2001 2.91125C15.7484 2.49057 15.1608 2.22687 14.532 2.16277C12.7458 1.98649 10.8951 1.79999 9.0001 1.79999C7.10506 1.79999 5.25437 1.98776 3.46819 2.16532C2.83946 2.22893 2.25169 2.4923 1.79998 2.91281C1.34827 3.33332 1.05905 3.88637 0.979106 4.4825C0.788258 5.94256 0.600098 7.45371 0.600098 8.99935C0.600098 10.545 0.788258 12.0561 0.979106 13.5175Z" stroke="#151621" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M1.01685 4.15039L7.33633 8.88695C7.81045 9.2421 8.39654 9.43523 9.00021 9.43523C9.60387 9.43523 10.19 9.2421 10.6641 8.88695L16.9836 4.15039" stroke="#151621" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>

                <input
                  type="email"
                  placeholder="Email"
                  required
                  ref={emailRef}
                  autoComplete="email"
                  className={"input-field" + (validationErrors?.email ? " input-error" : "")}
                />
              </div>

              {validationErrors?.email && (
                <div className="input-error-text">
                  {validationErrors?.email?.map((x) => (
                    <>
                      <span key={x} style={{ color: "red" }}>
                        {x}
                      </span>
                      <br />
                    </>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-2">
              <div className="relative">

                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="input-icon">
                <path d="M2.4367 14.1257C2.61593 15.5779 3.91563 16.7308 5.52279 16.8011C6.63365 16.8497 7.76842 16.875 9.00001 16.875C10.2316 16.875 11.3664 16.8497 12.4772 16.8011C14.0844 16.7308 15.3841 15.5779 15.5633 14.1257C15.8122 12.1088 15.8122 10.3912 15.5633 8.37428C15.3841 6.92213 14.0844 5.76914 12.4772 5.69885C11.3664 5.65026 10.2316 5.625 9.00001 5.625C7.76842 5.625 6.63365 5.65026 5.52279 5.69885C3.91563 5.76914 2.61593 6.92213 2.4367 8.37428C2.18776 10.3912 2.18777 12.1088 2.4367 14.1257Z" stroke="#151621" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M5.4751 5.5195V4.8C5.4751 3.82533 5.86228 2.89058 6.55148 2.20138C7.24068 1.51219 8.17543 1.125 9.1501 1.125C10.1248 1.125 11.0595 1.51219 11.7487 2.20138C12.4379 2.89058 12.8251 3.82533 12.8251 4.8V5.5195" stroke="#151621" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9.1499 10.05V12.3" stroke="#151621" stroke-width="1.2" stroke-linecap="round"/>
                </svg>

                <input
                  type="password"
                  placeholder="Password"
                  required
                  ref={passwordRef}
                  autoComplete="current-password"
                  className={"input-field input-field--with-icon" + (validationErrors?.password ? " input-error" : "")}
                />
              </div>
              
              {validationErrors?.password && (
                <div className="input-error-text">
                  {validationErrors?.password?.map((x) => (
                    <>
                      <span key={x} style={{ color: "red" }}>
                        {x}
                      </span>
                      <br />
                    </>
                  ))}
                </div>
              )}
            </div>
            
            {/*
            <div className="flex justify-end">
              <Link to="/forgot-password" className="color-purple">
                Forgot password?
              </Link>
            </div>
            */}

            <div className="text-center mt-6 pt-4">
              <button type="submit" className="button">
                {t("Login")}

                <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1.5 8H15.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M11.7307 12L15.4999 8L11.7307 4" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
